/**
 * Composant Dialog
 * Modal/dialogue avec overlay — rendu via Portal pour éviter les problèmes de stacking context
 */

import { type ReactNode, type KeyboardEvent as ReactKeyboardEvent, useEffect, useState, useCallback, useRef, useId, createContext, useContext } from 'react';
import { createPortal } from 'react-dom';
import { X } from 'lucide-react';
import { cn } from '@/lib/utils';

// ========================================
// Context
// ========================================

interface DialogContextType {
    isOpen: boolean;
    close: () => void;
    titleId: string;
    descriptionId: string;
}

const DialogContext = createContext<DialogContextType | null>(null);

// ========================================
// Dialog Root
// ========================================

interface DialogProps {
    open: boolean;
    onOpenChange: (open: boolean) => void;
    children: ReactNode;
}

function Dialog({ open, onOpenChange, children }: DialogProps): ReactNode {
    const uniqueId = useId();
    const titleId = `dialog-title-${uniqueId}`;
    const descriptionId = `dialog-desc-${uniqueId}`;

    // Fermer avec Escape
    useEffect(() => {
        const handleEscape = (e: KeyboardEvent) => {
            if (e.key === 'Escape' && open) {
                onOpenChange(false);
            }
        };

        document.addEventListener('keydown', handleEscape);
        return () => document.removeEventListener('keydown', handleEscape);
    }, [open, onOpenChange]);

    // Bloquer le scroll du body quand ouvert
    useEffect(() => {
        if (open) {
            document.body.style.overflow = 'hidden';
        } else {
            document.body.style.overflow = '';
        }
        return () => {
            document.body.style.overflow = '';
        };
    }, [open]);

    const close = useCallback(() => onOpenChange(false), [onOpenChange]);

    if (!open) return <>{null}</>;

    return (
        <DialogContext.Provider value={{ isOpen: open, close, titleId, descriptionId }}>
            {children}
        </DialogContext.Provider>
    );
}

// ========================================
// Dialog Overlay (standalone, not used by DialogContent)
// ========================================

interface DialogOverlayProps {
    className?: string;
}

function DialogOverlay({ className }: DialogOverlayProps): ReactNode {
    const context = useContext(DialogContext);
    if (!context) throw new Error('DialogOverlay must be used within Dialog');

    return (
        <div
            className={cn(
                'fixed inset-0 z-50 bg-black/50 backdrop-blur-sm',
                className
            )}
            onClick={context.close}
        />
    );
}

// ========================================
// Dialog Content — rendered via Portal
// ========================================

interface DialogContentProps {
    children: ReactNode;
    className?: string;
}

function DialogContent({ children, className }: DialogContentProps): ReactNode {
    const context = useContext(DialogContext);
    if (!context) throw new Error('DialogContent must be used within Dialog');

    const contentRef = useRef<HTMLDivElement>(null);
    const previousActiveElement = useRef<Element | null>(null);

    // Auto-focus first focusable element and restore focus on close
    useEffect(() => {
        previousActiveElement.current = document.activeElement;

        // Focus the dialog container itself so focus is inside the dialog
        const timer = setTimeout(() => {
            if (contentRef.current) {
                const focusable = contentRef.current.querySelector<HTMLElement>(
                    'button, [href], input, select, textarea, [tabindex]:not([tabindex="-1"])'
                );
                if (focusable) {
                    focusable.focus();
                } else {
                    contentRef.current.focus();
                }
            }
        }, 0);

        return () => {
            clearTimeout(timer);
            // Restore focus to the element that was focused before the dialog opened
            if (previousActiveElement.current instanceof HTMLElement) {
                previousActiveElement.current.focus();
            }
        };
    }, []);

    // Focus trapping
    const handleKeyDown = useCallback((e: ReactKeyboardEvent) => {
        if (e.key !== 'Tab' || !contentRef.current) return;

        const focusableElements = contentRef.current.querySelectorAll<HTMLElement>(
            'button, [href], input, select, textarea, [tabindex]:not([tabindex="-1"])'
        );

        if (focusableElements.length === 0) return;

        const firstElement = focusableElements[0];
        const lastElement = focusableElements[focusableElements.length - 1];

        if (e.shiftKey) {
            if (document.activeElement === firstElement) {
                e.preventDefault();
                lastElement.focus();
            }
        } else {
            if (document.activeElement === lastElement) {
                e.preventDefault();
                firstElement.focus();
            }
        }
    }, []);

    // Portal: render directly into document.body to escape any parent transform/stacking context
    return createPortal(
        <div
            className="fixed inset-0 z-50 flex items-center justify-center p-4 sm:p-6 bg-black/50 backdrop-blur-sm animate-in fade-in-0"
            style={{ position: 'fixed', top: 0, left: 0, right: 0, bottom: 0 }}
            onClick={context.close}
        >
            <div
                ref={contentRef}
                role="dialog"
                aria-modal="true"
                aria-labelledby={context.titleId}
                aria-describedby={context.descriptionId}
                tabIndex={-1}
                onKeyDown={handleKeyDown}
                className={cn(
                    'relative w-full max-w-3xl max-h-[85vh] sm:max-h-[90vh] overflow-auto',
                    'bg-white dark:bg-[#1E293B] rounded-lg shadow-xl p-4 sm:p-6',
                    'animate-in fade-in-0 zoom-in-95',
                    'focus:outline-none',
                    className
                )}
                onClick={(e) => e.stopPropagation()}
            >
                {children}
                <button
                    type="button"
                    onClick={context.close}
                    className={cn(
                        'absolute right-3 top-3 sm:right-4 sm:top-4 z-10 cursor-pointer',
                        'rounded-full p-2 bg-white/90 dark:bg-[#334155]/90 text-[#64748B] dark:text-[#94A3B8] shadow-sm border border-black/5 dark:border-white/5',
                        'hover:bg-white dark:hover:bg-[#334155] hover:text-[#1E293B] dark:hover:text-[#F1F5F9] focus:outline-none focus:ring-2 focus:ring-[#0F2744] focus:ring-offset-2',
                        'transition-all'
                    )}
                >
                    <X className="h-4 w-4" />
                    <span className="sr-only">Fermer</span>
                </button>
            </div>
        </div>,
        document.body
    );
}

// ========================================
// Dialog Header
// ========================================

interface DialogHeaderProps {
    children: ReactNode;
    className?: string;
}

function DialogHeader({ children, className }: DialogHeaderProps): ReactNode {
    return (
        <div className={cn('flex flex-col space-y-1.5 pb-4 pr-8', className)}>
            {children}
        </div>
    );
}

// ========================================
// Dialog Title
// ========================================

interface DialogTitleProps {
    children: ReactNode;
    className?: string;
}

function DialogTitle({ children, className }: DialogTitleProps): ReactNode {
    const context = useContext(DialogContext);

    return (
        <h2
            id={context?.titleId}
            className={cn('text-lg font-semibold leading-none tracking-tight', className)}
        >
            {children}
        </h2>
    );
}

// ========================================
// Dialog Description
// ========================================

interface DialogDescriptionProps {
    children: ReactNode;
    className?: string;
}

function DialogDescription({ children, className }: DialogDescriptionProps): ReactNode {
    const context = useContext(DialogContext);

    return (
        <p
            id={context?.descriptionId}
            className={cn('text-sm text-[#64748B]', className)}
        >
            {children}
        </p>
    );
}

// ========================================
// Dialog Footer
// ========================================

interface DialogFooterProps {
    children: ReactNode;
    className?: string;
}

function DialogFooter({ children, className }: DialogFooterProps): ReactNode {
    return (
        <div className={cn('flex flex-col-reverse sm:flex-row sm:justify-end gap-2 pt-4', className)}>
            {children}
        </div>
    );
}

// ========================================
// Hook pour dialogues simples
// ========================================

export function useDialog(initialState = false) {
    const [isOpen, setIsOpen] = useState(initialState);

    const open = useCallback(() => setIsOpen(true), []);
    const close = useCallback(() => setIsOpen(false), []);
    const toggle = useCallback(() => setIsOpen((prev) => !prev), []);

    return { isOpen, open, close, toggle, setIsOpen };
}

export {
    Dialog,
    DialogContent,
    DialogHeader,
    DialogTitle,
    DialogDescription,
    DialogFooter,
    DialogOverlay,
};
